import React, {useEffect, useRef, useState} from 'react';

import CSSModules from 'react-css-modules';
import {DefaultPlayer as Video} from 'react-html5video';

import {
  CLOUDFRONT_VIDEO_URL_PREFIX,
  CLOUDINARY_VIDEO_URL_PREFIX,
} from '@edume/bento/videoUtil';

import Button from '../Button';

import '../../resources/styles/ReactHtml5Video.css';
import styles from './styles.module.scss';

type VideoContentProps = {
  url: string;
  retryText: string;
  onFinish?: () => void;
  onPlay?: () => void;
  onToggleFullscreen?: () => void;
  isSkippable?: boolean;
  delayStart?: boolean;
  autoPlay?: boolean;
  videoStyle?: React.CSSProperties;
};

type VideoPlayerRef = {
  videoEl: HTMLVideoElement;
};

type CustomDocument = typeof document & {
  mozCancelFullscreen?: () => Promise<void>;
  msExitFullscreen?: () => Promise<void>;
  webkitExitFullscreen?: () => Promise<void>;
  mozFullscreenElement?: Element;
  msFullscreenElement?: Element;
  webkitFullscreenElement?: Element;
};

type CustomHTMLElement = HTMLElement & {
  msRequestFullscreen: () => Promise<void>;
  mozRequestFullscreen: () => Promise<void>;
  webkitRequestFullscreen: () => Promise<void>;
  webkitEnterFullscreen: () => Promise<void>;
};

const VideoContent = ({
  onFinish,
  onPlay,
  onToggleFullscreen,
  isSkippable = false,
  url,
  delayStart = false,
  autoPlay = false,
  retryText,
  videoStyle = {},
}: VideoContentProps) => {
  const [errored, setErrored] = useState(false);
  const [isIos] = useState(
    () =>
      [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod',
      ].includes(navigator.platform) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
  const videoPlayer = useRef<VideoPlayerRef>(null!);
  const delayTimeout = useRef<number | null>(null);
  const [isIosFullscreen, setIsIosFullscreen] = useState(false);
  const [urlLoadRetries, setUrlLoadRetries] = useState(0);

  useEffect(() => {
    if (autoPlay) {
      const timeout = delayStart ? 400 : 0;
      delayTimeout.current = window.setTimeout(() => {
        videoPlayer.current.videoEl.play();
      }, timeout);
    }
  }, [autoPlay, delayStart]);

  useEffect(
    () => () => {
      if (delayTimeout.current) {
        window.clearTimeout(delayTimeout.current);
      }
    },
    []
  );

  const onPlayVideo = () => {
    if (onPlay) {
      onPlay();
    }
  };

  const onEnded = () => {
    if (onFinish) {
      onFinish();
    }
  };

  const onFullscreenClick = () => {
    const videoEl = videoPlayer.current.videoEl
      .parentElement as CustomHTMLElement;

    const customDocument = document as CustomDocument;

    if (isSkippable || !isIos) {
      let fullscreenEl = videoEl;
      videoEl.requestFullscreen =
        // eslint-disable-next-line @typescript-eslint/unbound-method
        videoEl.requestFullscreen ||
        videoEl.msRequestFullscreen ||
        videoEl.mozRequestFullscreen ||
        videoEl.webkitRequestFullscreen ||
        videoEl.webkitEnterFullscreen;
      customDocument.exitFullscreen =
        // eslint-disable-next-line @typescript-eslint/unbound-method
        customDocument.exitFullscreen ||
        customDocument.msExitFullscreen ||
        customDocument.mozCancelFullscreen ||
        customDocument.webkitExitFullscreen;
      const fullscreenElement =
        customDocument.fullscreenElement ||
        customDocument.msFullscreenElement ||
        customDocument.mozFullscreenElement ||
        customDocument.webkitFullscreenElement;
      if (
        // iPhone Safari & Chrome
        !videoEl.requestFullscreen &&
        !videoEl.webkitEnterFullscreen &&
        !!(videoEl.children[0] as CustomHTMLElement).webkitEnterFullscreen
      ) {
        fullscreenEl = videoEl.children[0] as CustomHTMLElement;
        fullscreenEl.requestFullscreen = fullscreenEl.webkitEnterFullscreen;
      }
      if (fullscreenElement === fullscreenEl) {
        document.exitFullscreen();
      } else {
        fullscreenEl.requestFullscreen();
      }
    } else {
      setIsIosFullscreen((prev) => !prev);
      if (onToggleFullscreen) {
        onToggleFullscreen();
      }
    }
  };

  const reloadVideo = () => {
    if (videoPlayer.current) {
      videoPlayer.current.videoEl.load();
      setErrored(false);
    }
  };

  const getErrorContent = () => (
    <div styleName='error-overlay' style={videoStyle}>
      <div styleName='error-button'>
        <Button
          type='thirdary'
          onClick={reloadVideo}
          iconUrl='/resources/img/24_retry_white.svg'
          darkIconUrl='/resources/img/24_retry_dark.svg'
          noMarginTop
          noMarginLeft
        >
          {retryText}
        </Button>
      </div>
    </div>
  );

  const onError = (e: string) => {
    console.log('error loading video', e);

    /**
     * Safari automatically makes an initial range request (Range: bytes=0-1) to check if the server supports partial content requests.
     * We've sporadically seen issues with Safari being unable to load the video after the initial range request,
     * so in these cases we fall back to loading the video directly from Cloudinary.
     */
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isSafari && urlLoadRetries < 1) {
      console.log('Safari detected, falling back to Cloudinary');
      const newUrl = url.replace(
        CLOUDFRONT_VIDEO_URL_PREFIX,
        CLOUDINARY_VIDEO_URL_PREFIX
      );
      setUrlLoadRetries((prev) => prev + 1);

      if (videoPlayer.current) {
        const videoEl = videoPlayer.current.videoEl;
        const sourceEl = videoEl.querySelector('source');
        if (sourceEl) {
          sourceEl.src = newUrl;
          videoEl.load();
        }
      }
    } else {
      setErrored(true);
    }
  };

  const videoControls = ['PlayPause', 'Time', 'Volume', 'Fullscreen'];
  if (isSkippable) {
    videoControls.splice(2, 0, 'Seek');
  }

  return (
    <div
      styleName={`videoContainer${isIosFullscreen ? ' fullscreen' : ''}`}
      dir='ltr'
    >
      {errored && getErrorContent()}
      <Video
        controls={videoControls}
        autoPlay={autoPlay}
        playsInline
        webkit-playsInline
        ref={videoPlayer}
        key={url}
        width='100%'
        style={videoStyle}
        styleName='video'
        onPlay={onPlayVideo}
        onError={onError}
        onEnded={onEnded}
        onFullscreenClick={onFullscreenClick}
      >
        <source
          src={url}
          type={'video/mp4'}
          data-automation='video-preview-source'
        />
      </Video>
    </div>
  );
};

export default CSSModules(VideoContent, styles, {allowMultiple: true});
